import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  UserCircle2,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import type { CreditAnalysis, TimelineEntry } from '@/api/commercial/credit-analysis';
import { Button } from '@/components/ui/button';

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  SEM_RESTRICAO: 'Sem restrição',
  APENAS_A_VISTA: 'Somente à vista',
};

const formatPaymentMethods = (methods?: string[]): string => {
  if (!methods || methods.length === 0) return '-';
  return methods.map((m) => PAYMENT_METHOD_LABELS[m] || m).join(', ');
};

interface CreditAnalysisTimelineProps {
  creditAnalysisId: string | null;
  entries?: TimelineEntry[] | null;
  creditAnalysis?: CreditAnalysis | null;
}

const statusMessages: Record<string, string> = {
  NOT_STARTED: 'Análise ainda não iniciada.',
  PENDING_EXTERNAL_DOCS: 'Solicitação de envio de documentação feita ao cliente.',
  PENDING_ANALYSIS: 'Documentos enviados pelo cliente. Aguardando início da análise.',
  IN_ANALYSIS: 'Iniciado processo de análise de crédito.',
  APPROVED: 'Análise aprovada.',
  PARTIALLY_APPROVED: 'Análise parcialmente aprovada.',
  DOC_DECLINED: 'Documentação reprovada.',
  DECLINED: 'Análise reprovada.',
  EXPIRED: 'Análise expirada. Necessário reenviar.',
};

const statusColors: Record<string, string> = {
  NOT_STARTED: 'text-gray-500',
  PENDING_EXTERNAL_DOCS: 'text-yellow-600',
  PENDING_ANALYSIS: 'text-blue-400',
  IN_ANALYSIS: 'text-blue-600',
  APPROVED: 'text-green-600',
  PARTIALLY_APPROVED: 'text-yellow-600',
  DOC_DECLINED: 'text-red-500',
  DECLINED: 'text-red-700',
  EXPIRED: 'text-red-400',
};

const statusIconMap: Record<string, React.ReactNode> = {
  NOT_STARTED: <Clock className="w-8 h-8" />,
  PENDING_EXTERNAL_DOCS: <AlertCircle className="w-8 h-8" />,
  PENDING_ANALYSIS: <Clock className="w-8 h-8" />,
  IN_ANALYSIS: <UserCircle2 className="w-8 h-8" />,
  APPROVED: <CheckCircle2 className="w-8 h-8" />,
  PARTIALLY_APPROVED: <CheckCircle2 className="w-8 h-8" />,
  DOC_DECLINED: <XCircle className="w-8 h-8" />,
  DECLINED: <XCircle className="w-8 h-8" />,
  EXPIRED: <Clock className="w-8 h-8" />,
};

const getStatusText = (status: string) => {
  if (status === 'APPROVED') return 'APROVADA';
  if (status === 'DECLINED') return 'REPROVADA';
  return 'PARCIALMENTE APROVADA';
};

const formatDate = (date: Date | string | undefined) => {
  if (!date) return '--/--/---- --:--';

  try {
    return new Date(date).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Date formatting error:', error);
    return '';
  }
};

const isRecord = (metadata: unknown): metadata is Record<string, unknown> => {
  return metadata !== null && typeof metadata === 'object' && !Array.isArray(metadata);
};

const normalizeObservationsText = (text: string): string => {
  let normalized = text;
  // Normalize status in "Situação:" field
  normalized = normalized.replace(/Situação: APPROVED/g, 'Situação: Aprovada');
  normalized = normalized.replace(
    /Situação: PARTIALLY_APPROVED/g,
    'Situação: Parcialmente Aprovada',
  );
  normalized = normalized.replace(/Situação: DECLINED/g, 'Situação: Reprovada');
  // Normalize payment methods
  normalized = normalized.replace(/SEM_RESTRICAO/g, 'Sem restrição');
  normalized = normalized.replace(/APENAS_A_VISTA/g, 'Somente à vista');
  // Normalize NEXT_PURCHASE
  normalized = normalized.replace(
    /Nova análise a partir de: NEXT_PURCHASE/g,
    'Nova análise a partir de: Próxima Venda',
  );
  normalized = normalized.replace(/NEXT_PURCHASE/g, 'Próxima Venda');
  return normalized;
};

const ITEMS_PER_PAGE = 10;

export function CreditAnalysisTimeline({ entries, creditAnalysis }: CreditAnalysisTimelineProps) {
  const [currentPage, setCurrentPage] = useState(1);

  if (!entries || entries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Nenhum histórico disponível para esta análise.</p>
      </div>
    );
  }

  const totalPages = Math.ceil(entries.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedEntries = entries.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="space-y-6">
      {paginatedEntries.map((entry) => {
        // Handle historical entries (with proposal metadata)
        if (entry.type === 'historical') {
          // Handle historical entries without status
          if (!entry.status) {
            return (
              <li
                key={entry.id}
                className="flex items-start gap-4 p-4 border rounded-lg bg-white shadow-sm"
              >
                <div className="text-gray-500">
                  <Clock className="w-8 h-8" />
                </div>
                <div className="flex-1 space-y-2 text-sm">
                  <p className="text-gray-700">
                    {entry.observations
                      ? normalizeObservationsText(entry.observations)
                      : 'Entrada de histórico'}
                  </p>
                  <div className="text-xs text-gray-400">
                    <p>
                      Criado em: {formatDate(entry.timestamp)} por {entry.author.name}
                    </p>
                  </div>
                </div>
              </li>
            );
          }

          const proposal =
            isRecord(entry.metadata) && isRecord(entry.metadata.proposal)
              ? (entry.metadata.proposal as {
                  seq?: number;
                  clientName?: string;
                  creditLimitInCents?: number;
                  paymentMethods?: string[];
                  newAnalysisIn?: string;
                })
              : undefined;

          return (
            <li
              key={entry.id}
              className="flex items-start gap-4 p-4 border rounded-lg bg-white shadow-sm"
            >
              <span
                className={`${statusColors[entry.status]}`}
                title={statusMessages[entry.status]}
              >
                {statusIconMap[entry.status]}
              </span>

              <div className="flex-1 space-y-2 text-sm">
                <p className={`font-medium ${statusColors[entry.status]}`}>
                  {statusMessages[entry.status]}
                </p>

                {/* Show proposal data for approved/partially approved */}
                {proposal &&
                (entry.status === 'APPROVED' || entry.status === 'PARTIALLY_APPROVED') ? (
                  <div className="text-gray-700 space-y-1">
                    <p>
                      Proposta #{proposal.seq} - {proposal.clientName}
                    </p>
                    <p>
                      Limite aprovado: R${' '}
                      {proposal.creditLimitInCents != null
                        ? (proposal.creditLimitInCents / 100).toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : '-'}
                    </p>
                    <p>Formas de pagamento: {formatPaymentMethods(proposal.paymentMethods)}</p>
                    <p>
                      Nova análise disponível em:{' '}
                      {proposal.newAnalysisIn === 'NEXT_PURCHASE'
                        ? 'Na próxima compra'
                        : formatDate(proposal.newAnalysisIn)}
                    </p>
                    {entry.observations && (
                      <p>Observações: {normalizeObservationsText(entry.observations)}</p>
                    )}
                  </div>
                ) : (
                  // Declined or other - show observations
                  entry.observations && (
                    <div className="text-gray-700">
                      <p>{normalizeObservationsText(entry.observations)}</p>
                    </div>
                  )
                )}

                <div className="text-xs text-gray-400">
                  <p>
                    Criado em: {formatDate(entry.timestamp)} por {entry.author.name}
                  </p>
                </div>
              </div>
            </li>
          );
        }

        // Handle audit entries (existing behavior)
        const statusItem = Array.isArray(entry.metadata)
          ? entry.metadata.find((item: any) => item.field === 'status')
          : null;
        const status = statusItem?.after as string | undefined;

        if (!status || status === 'unknown') {
          // Show entry with generic handling when no status is found
          return (
            <li
              key={entry.id}
              className="flex items-start gap-4 p-4 border rounded-lg bg-white shadow-sm"
            >
              <div className="text-gray-500">
                <Clock className="w-8 h-8" />
              </div>
              <div className="flex-1 space-y-2 text-sm">
                <p className="text-gray-700">
                  {entry.observations
                    ? normalizeObservationsText(entry.observations)
                    : 'Entrada de histórico'}
                </p>
                <div className="text-xs text-gray-400">
                  <p>
                    Criado em: {formatDate(entry.timestamp)} por {entry.author.name}
                  </p>
                </div>
              </div>
            </li>
          );
        }

        return (
          <li
            key={entry.id}
            className="flex items-start gap-4 p-4 border rounded-lg bg-white shadow-sm"
          >
            {/* status icon */}
            <span className={`${statusColors[status]}`} title={statusMessages[status]}>
              {statusIconMap[status]}
            </span>

            {/* details */}
            <div className="flex-1 space-y-2 text-sm">
              {(() => {
                // PENDING_EXTERNAL_DOCS - primeira solicitação
                if (
                  statusItem?.before !== 'PENDING_EXTERNAL_DOCS' &&
                  statusItem?.after === 'PENDING_EXTERNAL_DOCS'
                ) {
                  return (
                    <p className={`font-medium ${statusColors[status]}`}>
                      {statusMessages[status]}
                    </p>
                  );
                }

                // PENDING_EXTERNAL_DOCS - reenvio
                if (
                  statusItem?.before === 'PENDING_EXTERNAL_DOCS' &&
                  statusItem?.after === 'PENDING_EXTERNAL_DOCS'
                ) {
                  return (
                    <p className={`font-medium ${statusColors[status]}`}>
                      Nova solicitação de envio de documentação feita ao cliente.
                    </p>
                  );
                }

                // Approved, Partially Approved, Declined - mostra detalhes completos
                if (['APPROVED', 'PARTIALLY_APPROVED', 'DECLINED'].includes(status)) {
                  // Check for proposal metadata in both audit and historical entries
                  const proposal =
                    isRecord(entry.metadata) && isRecord((entry.metadata as any).proposal)
                      ? ((entry.metadata as any).proposal as {
                          seq?: number;
                          clientName?: string;
                          creditLimitInCents?: number;
                          paymentMethods?: string[];
                          newAnalysisIn?: string;
                        })
                      : undefined;

                  return (
                    <>
                      <p className={`font-medium ${statusColors[status]}`}>
                        {statusMessages[status]}
                      </p>
                      <div className="text-gray-700 space-y-1">
                        {proposal ? (
                          <>
                            <p>
                              Proposta #{proposal.seq} - {proposal.clientName}
                            </p>
                            <p>
                              Limite aprovado: R${' '}
                              {proposal.creditLimitInCents != null
                                ? (proposal.creditLimitInCents / 100).toLocaleString('pt-BR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : '-'}
                            </p>
                            <p>
                              Formas de pagamento: {formatPaymentMethods(proposal.paymentMethods)}
                            </p>
                            <p>
                              Nova análise disponível em:{' '}
                              {proposal.newAnalysisIn === 'NEXT_PURCHASE'
                                ? 'Na próxima compra'
                                : formatDate(proposal.newAnalysisIn)}
                            </p>
                          </>
                        ) : (
                          <>
                            <p>Análise concluída como {`"${getStatusText(status)}"`}.</p>
                            <p>
                              Limite aprovado: R${' '}
                              {creditAnalysis?.creditLimitInCents != null
                                ? (creditAnalysis.creditLimitInCents / 100).toLocaleString(
                                    'pt-BR',
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )
                                : '-'}
                            </p>
                            <p>
                              Limite disponível: R${' '}
                              {creditAnalysis?.creditAvailableInCents != null
                                ? (creditAnalysis.creditAvailableInCents / 100).toLocaleString(
                                    'pt-BR',
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )
                                : '-'}
                            </p>
                            <p>
                              Formas de pagamento:{' '}
                              {formatPaymentMethods(creditAnalysis?.paymentMethods)}
                            </p>
                            <p>
                              Nova análise disponível em:{' '}
                              {creditAnalysis?.newAnalysisIn === 'NEXT_PURCHASE'
                                ? 'Na próxima compra'
                                : formatDate(creditAnalysis?.newAnalysisIn)}
                            </p>
                          </>
                        )}
                        {entry.observations && (
                          <p>Observações: {normalizeObservationsText(entry.observations)}</p>
                        )}
                      </div>
                    </>
                  );
                }

                // PENDING_ANALYSIS, IN_ANALYSIS, DOC_DECLINED, EXPIRED
                if (
                  ['PENDING_ANALYSIS', 'IN_ANALYSIS', 'DOC_DECLINED', 'EXPIRED'].includes(status)
                ) {
                  return (
                    <p className={`font-medium ${statusColors[status]}`}>
                      {statusMessages[status]}
                    </p>
                  );
                }

                // Default - mostra mensagem baseada no entry.status
                return (
                  <p className={`font-medium ${statusColors[status]}`}>
                    {statusMessages[status] || status}
                  </p>
                );
              })()}

              <div className="text-xs text-gray-400">
                <p>Criado em: {formatDate(entry.timestamp)}</p>
              </div>
            </div>
          </li>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-500">
            Página {currentPage} de {totalPages} ({entries.length} registros)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
